import { ChangeEvent } from "react";
import { useSelector } from "react-redux";

import { Specimen, SpecimenAdmin } from "pwapi";

import { QueueEnum } from "shared/reducers/casesReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import DateRangeInput from "components/customInputs/DateRangeInput";
import MultiColumnSelectInput from "components/customInputs/MultiColumnSelectInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import ApplicationState from "store/ApplicationState";

type AdvancedCaseFiltersProps = {
  receivedDateAfter: string;
  receivedDateBefore: string;
  patientDateOfBirth: string;
  externalAccession: string;
  grossCompletedAfter: string;
  grossCompletedBefore: string;
  accessionCompletedAfter: string;
  accessionCompletedBefore: string;
  slideUploadedAtAfter: string;
  slideUploadedAtBefore: string;
  finalizedAtAfter: string;
  finalizedAtBefore: string;
  diagnosesDiagnosis: string;
  note: string;
  accessionDateFrom: string;
  accessionDateTo: string;
  reportedDateFrom: string;
  reportedDateTo: string;
  specimensBiopsyType: string[];
  biopsyDate: string;
  diagnosisCategory: string[];
  diagnosesMicroscopic: string;
  comment: string;
  icd10: string;
  specimensType: string;
  specimensProcedure: string;
  specimensSite: string;
  specimensClinicalHistory: string;
  allSlidesSuccess: boolean;
  allOrderedDocumentsUploaded: boolean;
  hasFlaggedSlides: boolean;
  hasIncompleteOrders: boolean;
  hasUnviewedSlides: boolean;
  unviewed?: string;

  onChange: Function;
  onChangeDateRange: Function;
  onChangeCheckbox: Function;

  features?: FeatureFlags;
  queue?: QueueEnum;
};

const AdvancedCaseFilters = (props: AdvancedCaseFiltersProps) => {
  const features = useSelector(({ sys }: ApplicationState) => sys.features);

  return (
    <>
      <DateRangeInput
        id="receivedDate"
        label="Date Received"
        values={{
          from: {
            id: "receivedDateAfter",
            name: "received_date_after",
            value: props.receivedDateAfter,
          },
          to: {
            id: "receivedDateBefore",
            name: "received_date_before",
            value: props.receivedDateBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <CustomInput
        id="patientDateOfBirth"
        label="Patient Date of Birth"
        onClear={() => props.onChange("patientDateOfBirth", "")}
      >
        <DateInput
          value={props.patientDateOfBirth}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            props.onChange("patientDateOfBirth", e.target.value)
          }
          showPickerOnFocus
          placeholder=" "
        />
      </CustomInput>
      <TextInput
        id="externalAccession"
        name="externalAccession"
        value={props.externalAccession}
        label="External Accession"
        onChange={(e: any) =>
          props.onChange("externalAccession", e.target.value)
        }
        onClear={() => props.onChange("externalAccession", "")}
      />
      <DateRangeInput
        id="grossCompleted"
        label="Gross Completed"
        values={{
          from: {
            id: "grossCompletedAfter",
            name: "gross_completed_after",
            value: props.grossCompletedAfter,
          },
          to: {
            id: "grossCompletedBefore",
            name: "gross_completed_before",
            value: props.grossCompletedBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <DateRangeInput
        id="accessionCompleted"
        label="Accession Completed"
        values={{
          from: {
            id: "accessionCompletedAfter",
            name: "accession_completed_after",
            value: props.accessionCompletedAfter,
          },
          to: {
            id: "accessionCompletedBefore",
            name: "accession_completed_before",
            value: props.accessionCompletedBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <DateRangeInput
        id="slideUploadedAt"
        label="Slide Uploaded"
        values={{
          from: {
            id: "slideUploadedAtAfter",
            name: "slide_uploaded_at_after",
            value: props.slideUploadedAtAfter,
          },
          to: {
            id: "slideUploadedAtBefore",
            name: "slide_uploaded_at_before",
            value: props.slideUploadedAtBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <DateRangeInput
        id="finalizedAt"
        label="Sign Out Date"
        values={{
          from: {
            id: "finalizedAtAfter",
            name: "finalized_at_after",
            value: props.finalizedAtAfter,
          },
          to: {
            id: "finalizedAtBefore",
            name: "finalized_at_before",
            value: props.finalizedAtBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <TextInput
        id="diagnosesDiagnosis"
        name="diagnosesDiagnosis"
        value={props.diagnosesDiagnosis}
        label="Diagnosis"
        onChange={(e: any) =>
          props.onChange("diagnosesDiagnosis", e.target.value)
        }
        onClear={() => props.onChange("diagnosesDiagnosis", "")}
      />
      <TextInput
        id="note"
        name="note"
        value={props.note}
        label="Diagnosis Note"
        onChange={(e: any) => props.onChange("note", e.target.value)}
        onClear={() => props.onChange("note", "")}
      />
      {props.features?.accessionDateRangeFilter && (
        <DateRangeInput
          id="accessionDate"
          label="Accession Date"
          values={{
            from: {
              id: "accessionDateFrom",
              name: "accession_date_from",
              value: props.accessionDateFrom,
            },
            to: {
              id: "accessionDateTo",
              name: "accession_date_to",
              value: props.accessionDateTo,
            },
          }}
          onChangeDate={props.onChangeDateRange}
          isClearable
        />
      )}
      {props.features?.reportedDateRangeFilter && (
        <DateRangeInput
          id="reportedDate"
          label="Reported Date"
          values={{
            from: {
              id: "reportedDateFrom",
              name: "reported_date_from",
              value: props.reportedDateFrom,
            },
            to: {
              id: "reportedDateTo",
              name: "reported_date_to",
              value: props.reportedDateTo,
            },
          }}
          onChangeDate={props.onChangeDateRange}
          isClearable
        />
      )}
      {props.features?.adminWPLFilters && (
        <MultiColumnSelectInput
          label="WPL Biopsy Type"
          id="specimensBiopsyType"
          value={props.specimensBiopsyType}
          options={Object.values(SpecimenAdmin.biopsy_type)}
          onChange={(value: any) =>
            props.onChange("specimensBiopsyType", value)
          }
        />
      )}
      {props.features?.biopsyDateFilter && (
        <CustomInput
          label="Biopsy Date"
          id="biopsyDate"
          onClear={() => props.onChange("biopsyDate", "")}
        >
          <DateInput
            value={props.biopsyDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              props.onChange("biopsyDate", e.target.value)
            }
            showPickerOnFocus
            placeholder=" "
          />
        </CustomInput>
      )}
      <TextInput
        id="diagnosesMicroscopic"
        value={props.diagnosesMicroscopic}
        label="Micro"
        onChange={(e: any) =>
          props.onChange("diagnosesMicroscopic", e.target.value)
        }
        onClear={() => props.onChange("diagnosesMicroscopic", "")}
      />
      {props.features?.commentFilter && (
        <TextInput
          id="comment"
          value={props.comment}
          label="Comment"
          onChange={(e: any) => props.onChange("comment", e.target.value)}
          onClear={() => props.onChange("comment", "")}
        />
      )}
      {props.features?.icd10Filter && (
        <TextInput
          id="icd10"
          value={props.icd10}
          label="Icd10"
          onChange={(e: any) => props.onChange("icd10", e.target.value)}
          onClear={() => props.onChange("icd10", "")}
        />
      )}
      <SelectInput
        id="specimensType"
        label="Specimen Type"
        defaultLabel="All Types"
        options={Object.values(Specimen.type).map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.specimensType}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("specimensType", e.target.value || "")
        }
      />
      <TextInput
        id="specimensSite"
        value={props.specimensSite}
        label="Specimen Site"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("specimensSite", e.target.value || "")
        }
        onClear={() => props.onChange("specimensSite", "")}
      />
      <SelectInput
        id="specimenProcedure"
        label="Specimen Procedure"
        defaultLabel="All Procedures"
        options={Object.values(Specimen.procedure).map((v) => ({
          label: procedureEnumToLabel(v) || "",
          value: v,
        }))}
        value={props.specimensProcedure}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("specimensProcedure", e.target.value || "")
        }
      />
      <TextInput
        id="specimensClinicalHistory"
        value={props.specimensClinicalHistory}
        label="Clinical History"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("specimensClinicalHistory", e.target.value || "")
        }
        onClear={() => props.onChange("specimensClinicalHistory", "")}
      />
      {props.queue &&
        [QueueEnum.Active, QueueEnum.Working].includes(props.queue) && (
          <SelectInput
            id="unviewed"
            label="Viewed Status"
            defaultLabel="Select..."
            options={[
              { label: "Unviewed", value: "true" },
              { label: "Viewed", value: "false" },
            ]}
            value={props.unviewed || ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              props.onChange("unviewed", e.target.value || "")
            }
          />
        )}
      {props.queue &&
        [QueueEnum.Working, QueueEnum.Finalized].includes(props.queue) && (
          <div className="CustomInput CustomInput__container CustomInput__container--checkbox">
            <label className="checkboxFilter">
              <input
                type="checkbox"
                checked={props.hasIncompleteOrders}
                onChange={() => {
                  props.onChangeCheckbox("hasIncompleteOrders");
                }}
              />
              &nbsp;Has Incomplete Orders
            </label>
          </div>
        )}
      {props.queue &&
        [QueueEnum.Working, QueueEnum.Finalized].includes(props.queue) &&
        features?.filterFlaggedSlides && (
          <div className="CustomInput CustomInput__container CustomInput__container--checkbox">
            <label className="checkboxFilter">
              <input
                type="checkbox"
                checked={props.hasFlaggedSlides}
                onChange={() => {
                  props.onChangeCheckbox("hasFlaggedSlides");
                }}
              />
              &nbsp;Has Flagged Slides
            </label>
          </div>
        )}
      {props.queue &&
        [QueueEnum.Working, QueueEnum.Finalized].includes(props.queue) && (
          <div className="CustomInput CustomInput__container CustomInput__container--checkbox">
            <label className="checkboxFilter">
              <input
                type="checkbox"
                checked={props.hasUnviewedSlides}
                onChange={() => {
                  props.onChangeCheckbox("hasUnviewedSlides");
                }}
              />
              &nbsp;Has Unviewed Slides
            </label>
          </div>
        )}
    </>
  );
};

export default AdvancedCaseFilters;
